import * as React from "react";
import { Button, Input, notification, Select } from "antd";
import { Search, Loader } from "react-feather";
import { request } from "../../../services/cpln";
import { Audit } from "../../auditTrail/types";
import { getAuditTrailPresetsNew, getDefaultAuditTrailTimeNew, resolveSubjectName } from "../../auditTrail/utils";
import { ViewModal } from "../../../components/modals/viewModal";
import { AuditGridWrapper } from "../../../components/detail/auditGridWrapper";
import qs from "qs";
import { useLocation, useNavigate } from "react-router-dom";
import { BillingAccount } from "../../../schema/types/account";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";
import { selectDefaultValue, selectLabel, selectValue, setValue as setTimezoneValue } from "../../../store/timezoneSlice";
import {
  DateRangePicker,
  resolveExpressionPair,
  WithTimezone,
  parseDateRangeFromQueryObject,
  buildDateRangeQueryObject,
  timezoneToUrlValue,
  urlValueToTimezone,
} from "../../../newcomponents/datepicker";

interface Props {
  account: BillingAccount;
}
export const AuditTrail: React.FC<Props> = ({ account }) => {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectOrg);
  const timezoneValue = useAppSelector(selectValue);
  const timezoneLabel = useAppSelector(selectLabel);
  const localTimezone = useAppSelector(selectDefaultValue);

  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL params including timezone
  const queryObject = React.useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    // Only parse on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const { kind, name, subject } = queryObject;

  // Parse date range and timezone from URL using utilities
  const urlDateParams = React.useMemo(
    () =>
      parseDateRangeFromQueryObject(queryObject, {
        defaultFrom: null,
        defaultTo: null,
        defaultTimezone: timezoneToUrlValue(timezoneValue, localTimezone),
      }),
    // Only compute on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const urlFrom = urlDateParams.from;
  const urlTo = urlDateParams.to;
  const urlTimezone = urlDateParams.timezone;

  const [selectedKind, setSelectedKind] = React.useState("any");

  const [nameInput, setNameInput] = React.useState("");
  const [idInput, setIdInput] = React.useState("");
  const [subjectInput, setSubjectInput] = React.useState("");

  const [fromNew, setFromNew] = React.useState<string | null>(null);
  const [toNew, setToNew] = React.useState<string | null>(null);

  const [isUrlStateProcessed, setIsUrlStateProcessed] = React.useState(false);
  const [queried, setQueried] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [audits, setAudits] = React.useState<Audit[]>([]);
  const [showDetailsOf, setShowDetailsOf] = React.useState<null | string>(null);
  const nextLinkRef = React.useRef<string | undefined>(undefined);
  const itemsRef = React.useRef<HTMLDivElement>(null as any);
  const [isIdDisabled, setIsIdDisabled] = React.useState(false);
  const [isNameDisabled, setIsNameDisabled] = React.useState(false);

  React.useEffect(() => {
    if (isUrlStateProcessed) {
      fetchData();
    }
  }, [isUrlStateProcessed]);

  React.useEffect(() => {
    if (kind) {
      setSelectedKind(kind.toString());
    }
    if (name) {
      setNameInput(name.toString());
    }
    if (subject) {
      setSubjectInput(subject.toString());
    }

    // Initialize from/to from URL params
    if (urlFrom) {
      setFromNew(urlFrom);
    } else {
      setFromNew(getDefaultAuditTrailTimeNew());
    }
    if (urlTo) {
      setToNew(urlTo);
    }

    // Initialize timezone from URL if present
    if (urlTimezone) {
      const resolvedTimezone = urlValueToTimezone(urlTimezone, localTimezone);
      if (resolvedTimezone !== timezoneValue) {
        dispatch(setTimezoneValue(resolvedTimezone));
      }
    }

    setIsUrlStateProcessed(true);
  }, []);

  React.useEffect(() => {
    setIsIdDisabled(nameInput.length > 0);
    setIsNameDisabled(idInput.length > 0);
  }, [idInput, nameInput]);

  React.useEffect(() => {
    if (!queried || !isUrlStateProcessed) {
      return;
    }

    // Build base query object
    const baseQsObject: Record<string, unknown> = {};
    if (selectedKind) {
      baseQsObject.kind = selectedKind;
    }
    if (nameInput) {
      baseQsObject.name = nameInput;
    }
    if (subjectInput) {
      baseQsObject.subject = subjectInput;
    }
    if (idInput) {
      baseQsObject.id = idInput;
    }

    // Build query object with date range and timezone
    const qsObject = buildDateRangeQueryObject(
      {
        from: fromNew,
        to: toNew,
        timezone: timezoneToUrlValue(timezoneValue, localTimezone),
      },
      baseQsObject,
      { omitTimezoneWhenDefault: "UTC" } // Only include tz param when not UTC
    );

    const queryString = qs.stringify(qsObject, { arrayFormat: "comma", encode: false });
    navigate({ search: `?${queryString}` });
  }, [selectedKind, nameInput, subjectInput, idInput, fromNew, toNew, timezoneValue, localTimezone]);

  function onScroll_Items() {
    if (!nextLinkRef.current) return;
    const { scrollHeight, scrollTop, clientHeight } = itemsRef.current;
    const val = scrollHeight - (scrollTop + clientHeight);
    if (val < 1) {
      fetchData();
    }
  }

  function onQuery() {
    nextLinkRef.current = undefined;
    setAudits([]);
    fetchData();
  }

  async function fetchData() {
    try {
      setIsLoading(true);
      let url = `/audit/account/${account.id}`;
      if (nextLinkRef.current) {
        url = nextLinkRef.current;
      } else {
        let params: string[] = [];
        if (nameInput.length > 0) {
          url += `/resource/name/${nameInput}`;
        }
        if (idInput.length > 0) {
          url += `/resource/id/${idInput}`;
        }
        if (selectedKind !== "any") {
          params.push(`resourceType=${selectedKind}`);
        }
        if (subjectInput.length > 0) {
          params.push(`subjectName=${resolveSubjectName(subjectInput, org)}`);
        }
        // Resolve from/to together to handle start/end cross-references
        const resolved = resolveExpressionPair(fromNew, toNew, timezoneValue);
        if (resolved.from) {
          params.push(`fromEvent=${resolved.from.toISOString()}`);
        }
        if (resolved.to) {
          params.push(`toEvent=${resolved.to.toISOString()}`);
        }
        if (params.length > 0) {
          url += `?${params.join("&")}`;
        }
      }
      const { data } = await request({ service: "audit", url });
      if (nextLinkRef.current) {
        setAudits([...audits, ...data.items]);
      } else {
        setAudits(data.items);
      }
      const _nextLink = data.links.find((l: any) => l.rel === "next")?.href;
      if (data.items.length === 100) {
        nextLinkRef.current = _nextLink;
      } else {
        nextLinkRef.current = undefined;
      }
      setQueried(true);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }

  function onClear() {
    setSelectedKind("any");
    setNameInput("");
    setSubjectInput("");
    setIdInput("");
    setFromNew(getDefaultAuditTrailTimeNew());
    setToNew(null);
  }

  return (
    <>
      <div className="mb-4">Audit Trail</div>
      <div className="flex items-center mb-2">
        <Select
          className="mr-2"
          style={{ width: 150 }}
          placeholder={"Kind"}
          value={selectedKind}
          onChange={setSelectedKind}
          options={[
            { label: "Any Kind", value: "any" },
            { label: "Billing Account", value: "account" },
            { label: "Alert", value: "alert" },
            { label: "Org", value: "org" },
            { label: "Payment Method", value: "payment-method" },
            { label: "User", value: "user" },
          ]}
        />
        <Input
          className="mr-2"
          style={{ width: 450 }}
          value={nameInput}
          placeholder={"Resource Name"}
          onChange={(e) => setNameInput(e.target.value)}
          disabled={isNameDisabled}
        />
        <Input
          className="flex-grow basis-0 mr-2"
          value={subjectInput}
          placeholder={"Subject (email or service account name)"}
          onChange={(e) => setSubjectInput(e.target.value)}
        />
        <Button style={{ width: 150 }} type={"primary"} onClick={onQuery} disabled={isLoading} loading={isLoading}>
          Query
        </Button>
      </div>
      <div className="flex items-center mb-2">
        <div className="mr-2" style={{ width: 150 }} />
        <Input
          className="mr-2"
          style={{ width: 450 }}
          value={idInput}
          placeholder={`Resource ID`}
          onChange={(e) => setIdInput(e.target.value)}
          disabled={isIdDisabled}
        />
        <span className="mr-2">Time Range ({timezoneLabel})</span>
        <WithTimezone>
          <DateRangePicker
            presets={getAuditTrailPresetsNew()}
            allowClear={[false, true]}
            placeholder={["From", "To"]}
            granularity="second"
            from={fromNew}
            setFrom={setFromNew}
            to={toNew}
            setTo={setToNew}
          />
        </WithTimezone>
        <Button
          style={{ width: 150 }}
          onClick={() => {
            onClear();
          }}
          type={"default"}
          disabled={isLoading}
        >
          Clear All Filters
        </Button>
      </div>
      {isLoading && !nextLinkRef.current && (
        <div className="flex flex-col items-center p-4 border" style={{ borderRadius: 6 }}>
          <Loader className="mb-2 animate-spin" />
          <div className="text-center text-2xl">Loading Audit Events</div>
        </div>
      )}
      {audits.length < 1 && queried && !isLoading ? (
        <div className="flex flex-col items-center p-4 border">
          <Search className="mb-2" />
          <div className="text-center text-2xl mt-2">No Audit Events Found</div>
        </div>
      ) : null}
      <AuditGridWrapper
        audits={audits}
        diffSupport={false}
        emptySpaceValue={470}
        onDetail={(id) => setShowDetailsOf(id)}
        onScroll={onScroll_Items}
        queried={queried}
      />
      {showDetailsOf ? (
        <ViewModal
          object={audits.find((a) => a.id === showDetailsOf)}
          title={"Audit Log"}
          onClose={() => setShowDetailsOf(null)}
          visible={!!showDetailsOf}
          filename={`audit-${audits.find((a) => a.id === showDetailsOf)?.id}`}
        />
      ) : null}
    </>
  );
};
